/**
 * TimeframeSelector — CloudWatch-style time range selector.
 *
 * Top bar:  1S · 2S · 3S | 1M · 3M · 6M | 1A  +  Custom button
 * Popover:  Absolute (double-calendar + date/time inputs)
 *           Relative (grid: Semaines / Mois / Année)
 */

import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TimeframeValue =
  | { kind: 'relative'; days: number }
  | { kind: 'absolute'; from: string; to: string };

export function resolveTimeframe(v: TimeframeValue): { from: string; to: string } {
  if (v.kind === 'absolute') return { from: v.from, to: v.to };
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - v.days);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function formatLabel(v: TimeframeValue): string {
  if (v.kind === 'relative') {
    const item = QUICK_ITEMS.find((q) => q.days === v.days);
    if (item) return item.label;
    if (v.days % 30 === 0) return `${v.days / 30} mois`;
    if (v.days % 7 === 0) return `${v.days / 7} sem.`;
    return `${v.days} j`;
  }
  const fmt = (d: string) =>
    new Date(d + 'T00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${fmt(v.from)} → ${fmt(v.to)}`;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUICK_ITEMS = [
  { label: '1S', days: 7 },
  { label: '2S', days: 14 },
  { label: '3S', days: 21 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1A', days: 365 },
];

const RELATIVE_ROWS = [
  { label: 'Semaines', items: [{ label: '1', days: 7 }, { label: '2', days: 14 }, { label: '3', days: 21 }] },
  { label: 'Mois',     items: [{ label: '1', days: 30 }, { label: '3', days: 90 }, { label: '6', days: 180 }] },
  { label: 'Année',    items: [{ label: '1', days: 365 }] },
];

const DAY_LABELS = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
const MONTH_LABELS = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin',
                      'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

// ---------------------------------------------------------------------------
// Calendar helpers
// ---------------------------------------------------------------------------

function toYMD(d: Date) {
  return d.toISOString().slice(0, 10);
}

function addMonths(year: number, month: number, n: number) {
  const d = new Date(year, month + n, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

// ---------------------------------------------------------------------------
// Single calendar month
// ---------------------------------------------------------------------------

interface CalMonthProps {
  year: number;
  month: number;
  rangeStart: string;
  rangeEnd: string;
  hover: string;
  onDayClick: (ymd: string) => void;
  onDayHover: (ymd: string) => void;
  maxDate: string; // YYYY-MM-DD, inclusive
  onPrev?: () => void;
  onNext?: () => void;
}

function CalMonth({ year, month, rangeStart, rangeEnd, hover, onDayClick, onDayHover, maxDate, onPrev, onNext }: CalMonthProps) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDow = getFirstDayOfWeek(year, month);
  const today = toYMD(new Date());

  // Effective range (hover preview when only start is set)
  const lo = rangeStart && rangeEnd ? rangeStart : (rangeStart && hover ? [rangeStart, hover].sort()[0] : rangeStart);
  const hi = rangeStart && rangeEnd ? rangeEnd   : (rangeStart && hover ? [rangeStart, hover].sort()[1] : '');

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Box sx={{ width: 224, userSelect: 'none' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <IconButton size="small" onClick={onPrev} disabled={!onPrev} sx={{ opacity: onPrev ? 1 : 0 }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" fontWeight={600}>
          {MONTH_LABELS[month]} {year}
        </Typography>
        <IconButton size="small" onClick={onNext} disabled={!onNext} sx={{ opacity: onNext ? 1 : 0 }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Day-of-week labels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.25 }}>
        {DAY_LABELS.map((l) => (
          <Typography key={l} variant="caption" align="center" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
            {l}
          </Typography>
        ))}
      </Box>

      {/* Day grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
        {cells.map((d, idx) => {
          if (d === null) return <Box key={idx} />;
          const ymd = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const isDisabled = ymd > maxDate;
          const isToday = ymd === today;
          const isStart = ymd === rangeStart;
          const isEnd = ymd === rangeEnd || (rangeStart && !rangeEnd && ymd === hover);
          const inRange = lo && hi && ymd > lo && ymd < hi;
          const isSelected = isStart || isEnd;

          return (
            <Box
              key={d}
              onClick={() => !isDisabled && onDayClick(ymd)}
              onMouseEnter={() => !isDisabled && onDayHover(ymd)}
              sx={{
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: isSelected ? 1 : inRange ? 0 : '50%',
                bgcolor: isSelected ? 'primary.main' : inRange ? 'primary.50' : 'transparent',
                color: isSelected ? 'primary.contrastText' : isDisabled ? 'text.disabled' : isToday ? 'primary.main' : 'text.primary',
                fontWeight: isToday && !isSelected ? 700 : 400,
                fontSize: '0.78rem',
                cursor: isDisabled ? 'default' : 'pointer',
                '&:hover': isDisabled ? {} : { bgcolor: isSelected ? 'primary.dark' : 'action.hover', borderRadius: 1 },
                transition: 'background-color 0.1s',
              }}
            >
              {d}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface TimeframeSelectorProps {
  value: TimeframeValue;
  onChange: (v: TimeframeValue) => void;
  maxDays?: number;
}

export default function TimeframeSelector({ value, onChange, maxDays = 90 }: TimeframeSelectorProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'absolute' | 'relative'>('relative');

  // Absolute draft state
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [hover, setHover] = useState('');
  const [startTime, setStartTime] = useState('00:00:00');
  const [endTime, setEndTime] = useState('23:59:59');
  const [absError, setAbsError] = useState('');

  // Calendar navigation — left calendar month
  const [navYear, setNavYear] = useState(new Date().getFullYear());
  const [navMonth, setNavMonth] = useState(new Date().getMonth());
  const right = addMonths(navYear, navMonth, 1);

  const maxDate = (() => {
    const d = new Date();
    return toYMD(d);
  })();

  // Open popover — seed draft from current value
  const handleOpen = useCallback(() => {
    if (value.kind === 'absolute') {
      setRangeStart(value.from);
      setRangeEnd(value.to);
      setTab('absolute');
    } else {
      setRangeStart('');
      setRangeEnd('');
      setTab('relative');
    }
    setAbsError('');
    setOpen(true);
  }, [value]);

  // Day click: first click = start, second = end (enforce start ≤ end)
  const handleDayClick = useCallback((ymd: string) => {
    if (!rangeStart || rangeEnd) {
      setRangeStart(ymd);
      setRangeEnd('');
    } else {
      const [lo, hi] = [rangeStart, ymd].sort();
      setRangeStart(lo);
      setRangeEnd(hi);
    }
  }, [rangeStart, rangeEnd]);

  const handleApply = useCallback(() => {
    if (!rangeStart || !rangeEnd) {
      setAbsError('Sélectionnez une date de début et une date de fin.');
      return;
    }
    const from = rangeStart;
    const to = rangeEnd;
    const diff = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000);
    if (diff > maxDays) {
      setAbsError(`Données disponibles sur ${maxDays} jours maximum.`);
      return;
    }
    onChange({ kind: 'absolute', from, to });
    setOpen(false);
  }, [rangeStart, rangeEnd, maxDays, onChange]);

  const handleClear = useCallback(() => {
    onChange({ kind: 'relative', days: 30 });
    setRangeStart('');
    setRangeEnd('');
    setOpen(false);
  }, [onChange]);

  const activeQuick = value.kind === 'relative' ? value.days : null;

  return (
    <>
      {/* ── Quick bar ── */}
      <Box
        ref={anchorRef}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'hidden',
          height: 32,
        }}
      >
        {QUICK_ITEMS.map((item, idx) => {
          const disabled = item.days > maxDays;
          const active = activeQuick === item.days;
          const isLastBeforeGroup =
            (idx === 2) || // after 3S
            (idx === 5);   // after 6M
          return (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="button"
                onClick={() => !disabled && onChange({ kind: 'relative', days: item.days })}
                sx={{
                  border: 'none',
                  background: 'none',
                  cursor: disabled ? 'default' : 'pointer',
                  px: 1.25,
                  height: 32,
                  fontFamily: 'inherit',
                  fontSize: '0.78rem',
                  fontWeight: active ? 700 : 400,
                  color: active ? 'primary.main' : disabled ? 'text.disabled' : 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.1s',
                  '&:hover': disabled ? {} : { color: 'text.primary' },
                }}
              >
                {item.label}
              </Box>
              {isLastBeforeGroup && (
                <Box sx={{ width: 1, height: 16, bgcolor: 'divider' }} />
              )}
            </Box>
          );
        })}

        {/* Divider before Custom */}
        <Box sx={{ width: 1, height: 16, bgcolor: 'divider' }} />

        {/* Custom button */}
        <Box
          component="button"
          onClick={handleOpen}
          sx={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            px: 1.25,
            height: 32,
            fontFamily: 'inherit',
            fontSize: '0.78rem',
            fontWeight: value.kind === 'absolute' ? 700 : 400,
            color: value.kind === 'absolute' ? 'primary.main' : 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { color: 'text.primary' },
          }}
        >
          {value.kind === 'absolute' ? formatLabel(value) : 'Personnaliser'}
          <CalendarTodayOutlinedIcon sx={{ fontSize: '0.85rem' }} />
        </Box>
      </Box>

      {/* ── Popover ── */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 1.5, boxShadow: 4, overflow: 'hidden', minWidth: 480 } } }}
      >
        {/* Tab toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, pb: 0 }}>
          <ToggleButtonGroup
            value={tab}
            exclusive
            size="small"
            onChange={(_, v) => v && setTab(v)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, py: 0.5, fontSize: '0.8rem', fontWeight: 500 } }}
          >
            <ToggleButton value="absolute">Absolue</ToggleButton>
            <ToggleButton value="relative">Relative</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mt: 1.5 }} />

        {/* ── Absolute tab ── */}
        {tab === 'absolute' && (
          <Box sx={{ p: 2 }}>
            {/* Two-month calendars */}
            <Box
              sx={{ display: 'flex', gap: 2, mb: 2 }}
              onMouseLeave={() => setHover('')}
            >
              <CalMonth
                year={navYear} month={navMonth}
                rangeStart={rangeStart} rangeEnd={rangeEnd} hover={hover}
                onDayClick={handleDayClick}
                onDayHover={setHover}
                maxDate={maxDate}
                onPrev={() => { const p = addMonths(navYear, navMonth, -1); setNavYear(p.year); setNavMonth(p.month); }}
              />
              <Divider orientation="vertical" flexItem />
              <CalMonth
                year={right.year} month={right.month}
                rangeStart={rangeStart} rangeEnd={rangeEnd} hover={hover}
                onDayClick={handleDayClick}
                onDayHover={setHover}
                maxDate={maxDate}
                onNext={() => { const n = addMonths(navYear, navMonth, 1); setNavYear(n.year); setNavMonth(n.month); }}
              />
            </Box>

            {/* Date/time inputs */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>Date de début</Typography>
                <TextField
                  size="small" fullWidth
                  placeholder="AAAA/MM/JJ"
                  value={rangeStart}
                  onChange={(e) => { setRangeStart(e.target.value); setAbsError(''); }}
                  inputProps={{ style: { fontSize: '0.8rem' } }}
                />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.67rem' }}>Format AAAA/MM/JJ</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>Heure de début</Typography>
                <TextField
                  size="small" fullWidth
                  placeholder="hh:mm:ss"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  inputProps={{ style: { fontSize: '0.8rem' } }}
                />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.67rem' }}>Format 24h</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>Date de fin</Typography>
                <TextField
                  size="small" fullWidth
                  placeholder="AAAA/MM/JJ"
                  value={rangeEnd}
                  onChange={(e) => { setRangeEnd(e.target.value); setAbsError(''); }}
                  inputProps={{ style: { fontSize: '0.8rem' } }}
                />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.67rem' }}>Format AAAA/MM/JJ</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>Heure de fin</Typography>
                <TextField
                  size="small" fullWidth
                  placeholder="hh:mm:ss"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  inputProps={{ style: { fontSize: '0.8rem' } }}
                />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.67rem' }}>Format 24h</Typography>
              </Box>
            </Box>

            {absError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{absError}</Typography>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button size="small" variant="outlined" onClick={handleClear}>Effacer</Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => setOpen(false)}>Annuler</Button>
                <Button size="small" variant="contained" onClick={handleApply} disableElevation>Appliquer</Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Relative tab ── */}
        {tab === 'relative' && (
          <Box sx={{ p: 2 }}>
            {RELATIVE_ROWS.map((row) => (
              <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 72, flexShrink: 0, fontWeight: 500 }}
                >
                  {row.label}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {row.items.map((item) => {
                    const disabled = item.days > maxDays;
                    const active = value.kind === 'relative' && value.days === item.days;
                    return (
                      <Box
                        key={item.label}
                        component="button"
                        onClick={() => {
                          if (disabled) return;
                          onChange({ kind: 'relative', days: item.days });
                          setOpen(false);
                        }}
                        sx={{
                          width: 44,
                          height: 32,
                          border: '1px solid',
                          borderColor: active ? 'primary.main' : 'divider',
                          borderRadius: 0.75,
                          bgcolor: active ? 'primary.main' : 'background.paper',
                          color: active ? 'primary.contrastText' : disabled ? 'text.disabled' : 'text.primary',
                          cursor: disabled ? 'default' : 'pointer',
                          fontFamily: 'inherit',
                          fontSize: '0.82rem',
                          fontWeight: active ? 600 : 400,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.1s',
                          '&:hover': disabled || active ? {} : {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                          },
                        }}
                      >
                        {item.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
}
